import { useEffect, useState } from 'react';

/**
 * Returns a value that lags `value` by `delayMs` of inactivity. The first
 * render passes through synchronously (so initial mount isn't delayed); each
 * subsequent change resets the timer and only commits after the value has been
 * stable for `delayMs`. Used by RenderedPreview to coalesce keystroke storms
 * into one render per pause (spec §4.1).
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (Object.is(debounced, value)) return;
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs, debounced]);
  return debounced;
}
